import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";

const radiusScale = d3.scaleSqrt().range([1, 50]).domain([0, 100]);
const width = 350;

interface BubbleChartProps {
  data: any[];
  chartId: string;
  activeBubble: { chartId: string; index: number } | null;
  setActiveBubble: React.Dispatch<
    React.SetStateAction<{ chartId: string; index: number } | null>
  >;
}

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    if ((current + " " + word).trim().length > maxChars) {
      lines.push(current.trim());
      current = word;
    } else {
      current += " " + word;
    }
  }
  if (current) lines.push(current.trim());
  return lines;
}

const BubbleChart: FC<BubbleChartProps> = ({
  data,
  chartId,
  activeBubble,
  setActiveBubble,
}) => {
  const [nodes, setNodes] = useState(data);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const simulation = d3
      .forceSimulation(nodes)
      .velocityDecay(0.5)
      .force("x", d3.forceX().strength(0.05))
      .force("y", d3.forceY().strength(0.05))
      .force(
        "collide",
        d3.forceCollide((d: any) => radiusScale(d.occurence / 3.5) + 11)
      )
      .on("tick", () => {
        setNodes([...nodes]);
      });

    return () => {
      simulation.stop();
    };
  }, [data]);

  const bringToFront = (index: number) => {
    if (!svgRef.current) return;
    const g = svgRef.current.querySelector(`#${chartId}-bubble-${index}`);
    if (g && g.parentNode) g.parentNode.appendChild(g);
  };

  const handleBubbleActivate = useCallback(
    (index: number) => {
      setActiveBubble({ chartId, index });
      bringToFront(index);
    },
    [chartId, setActiveBubble]
  );

  const handleBubbleDeactivate = useCallback(() => {
    setActiveBubble(null);
  }, [setActiveBubble]);

  // More aggressive prevention of all touch behaviors that could cause scrolling
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Also prevent default on the event target to be extra sure
    if (e.currentTarget) {
      const target = e.currentTarget as Element;
      target.addEventListener("touchmove", preventDefault as EventListener, {
        passive: false,
      });
      target.addEventListener("touchend", cleanup as EventListener, {
        once: true,
      });
    }
  }, []);

  const preventDefault = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  const cleanup = useCallback(
    (e: Event) => {
      const target = e.currentTarget as Element;
      if (target) {
        target.removeEventListener(
          "touchmove",
          preventDefault as EventListener
        );
      }
    },
    [preventDefault]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      handleBubbleActivate(index);
    },
    [handleBubbleActivate]
  );

  const height = nodes.length < 9 ? 250 : 350;

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      style={{
        overflow: "visible",
        marginLeft: "-30px",
        touchAction: "none", // CSS property to disable touch behaviors
      }}
    >
      <defs>
        <filter id="dropshadow2" height="150%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
          <feOffset in="blur" dx="0" dy="4" result="offsetBlur" />
          <feFlood floodColor="#000000bf" floodOpacity="0.5" result="color" />
          <feComposite
            in="color"
            in2="offsetBlur"
            operator="in"
            result="shadow"
          />
          <feMerge>
            <feMergeNode in="shadow" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {nodes.map((item, index) => {
          const isActive =
            activeBubble?.chartId === chartId && activeBubble.index === index;

          const bubbleRadius = isActive
            ? 60
            : 10 + radiusScale(item.occurence / 4);

          return (
            <g
              id={`${chartId}-bubble-${index}`}
              key={`bubble-${item.name}-${index}`}
              transform={`translate(${item.x}, ${item.y})`}
              onMouseEnter={() => handleBubbleActivate(index)}
              onMouseLeave={handleBubbleDeactivate}
              onTouchStart={handleTouchStart}
              onTouchEnd={(e) => handleTouchEnd(e, index)}
              style={{
                cursor: "pointer",
                touchAction: "none",
                userSelect: "none",
              }}
            >
              <circle
                r={bubbleRadius}
                fill="white"
                filter="url(#dropshadow2)"
                style={{
                  transition: "r 0.25s ease-out",
                }}
              />
              {isActive ? (
                <text
                  x={0}
                  y={0}
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  style={{ fontSize: 12, pointerEvents: "none" }}
                >
                  <tspan x={0} dy="-26" fontWeight="bold" fontSize={16}>
                    {item.name}
                  </tspan>
                  {wrapText(item.description, 23).map((line, i) => (
                    <tspan key={i} x={0} dy="1.2em">
                      {line}
                    </tspan>
                  ))}
                </text>
              ) : (
                <image
                  x={-(item.occurence === 30 ? 12.5 : 20)}
                  y={-(item.occurence === 30 ? 12.5 : 20)}
                  href={item.link}
                  height={item.occurence === 30 ? 25 : 40}
                  width={item.occurence === 30 ? 25 : 40}
                  style={{ transition: "all 0.25s ease-out" }}
                />
              )}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default BubbleChart;
